// Rep Activity — management view of what each salesperson has been doing, built entirely
// on the CRM Activity log (email/calendar sync + call logging + tasks). Managers see a
// leaderboard across reps and can drill into any rep; a rep sees only their own activity.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Mail, PhoneCall, CalendarDays, CheckSquare, StickyNote, ArrowLeft, Plus } from "lucide-react";

interface RepRow {
  user: { id: string; name: string; role: string };
  emails: number; calls: number; meetings: number; tasks: number; notes: number; total: number;
}
interface SummaryResp { mode: "summary"; range: { from: string; to: string }; reps: RepRow[] }
interface RecentItem {
  id: string; type: string; subject: string; disposition: string | null;
  completedAt: string | null; createdAt: string; source: string | null;
  contact?: { id: string; name: string } | null;
  lead?: { id: string; name: string } | null;
  deal?: { id: string; name: string } | null;
}
interface UserResp {
  mode: "user";
  range: { from: string; to: string };
  user: { id: string; name: string; role: string };
  totals: {
    emails: number; calls: number; meetings: number; notes: number; tasks: number;
    tasksCompleted: number; tasksOpen: number; leadsTouched: number; contactsTouched: number; dealsAdvanced: number;
  };
  callsByDisposition: Record<string, number>;
  recent: RecentItem[];
}
type Resp = SummaryResp | UserResp;

function isoDate(d: Date) { return d.toISOString().slice(0, 10); }

const TYPE_ICON: Record<string, JSX.Element> = {
  EMAIL: <Mail className="h-4 w-4 text-blue-500" />,
  CALL: <PhoneCall className="h-4 w-4 text-green-600" />,
  MEETING: <CalendarDays className="h-4 w-4 text-purple-500" />,
  TASK: <CheckSquare className="h-4 w-4 text-amber-600" />,
  NOTE: <StickyNote className="h-4 w-4 text-muted-foreground" />,
};

export default function RepActivityPage() {
  const qc = useQueryClient();
  const [from, setFrom] = useState(() => isoDate(new Date(Date.now() - 30 * 86_400_000)));
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["/reports/rep-activity", from, to, selectedUserId],
    queryFn: async () =>
      (await api.get("/reports/rep-activity", {
        params: { from, to, userId: selectedUserId || undefined },
      })).data as Resp,
  });

  return (
    <>
      <PageHeader
        title="Rep Activity"
        description="Per-rep rollup of emails, calls, meetings, tasks and deals — pulled automatically from the CRM activity log."
      />
      <div className="p-6 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="from">From</Label>
            <Input id="from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-9" />
          </div>
          <div>
            <Label htmlFor="to">To</Label>
            <Input id="to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="h-9" />
          </div>
          {data?.mode === "user" && selectedUserId && (
            <Button variant="outline" size="sm" onClick={() => setSelectedUserId(null)}>
              <ArrowLeft className="mr-1 h-4 w-4" />All reps
            </Button>
          )}
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {data?.mode === "summary" && (
          <Card>
            <CardContent className="p-0">
              <Table>
                <Thead>
                  <Tr><Th>Rep</Th><Th>Role</Th><Th className="text-right">Emails</Th><Th className="text-right">Calls</Th><Th className="text-right">Meetings</Th><Th className="text-right">Tasks</Th><Th className="text-right">Total</Th></Tr>
                </Thead>
                <Tbody>
                  {data.reps.length === 0 && <Tr><Td colSpan={7} className="py-8 text-center text-muted-foreground">No activity in this range.</Td></Tr>}
                  {data.reps.map((r) => (
                    <Tr key={r.user.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedUserId(r.user.id)}>
                      <Td className="font-medium hover:underline">{r.user.name}</Td>
                      <Td className="text-xs text-muted-foreground">{r.user.role.replace(/_/g, " ").toLowerCase()}</Td>
                      <Td className="text-right tabular-nums">{r.emails}</Td>
                      <Td className="text-right tabular-nums">{r.calls}</Td>
                      <Td className="text-right tabular-nums">{r.meetings}</Td>
                      <Td className="text-right tabular-nums">{r.tasks}</Td>
                      <Td className="text-right font-semibold tabular-nums">{r.total}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </CardContent>
          </Card>
        )}

        {data?.mode === "user" && <RepDetail data={data} qc={qc} />}
      </div>
    </>
  );
}

function RepDetail({ data, qc }: { data: UserResp; qc: ReturnType<typeof useQueryClient> }) {
  const t = data.totals;
  const cards = [
    { label: "Emails", value: t.emails, icon: TYPE_ICON.EMAIL },
    { label: "Calls", value: t.calls, icon: TYPE_ICON.CALL },
    { label: "Meetings", value: t.meetings, icon: TYPE_ICON.MEETING },
    { label: "Tasks done", value: t.tasksCompleted, icon: TYPE_ICON.TASK },
    { label: "Tasks open", value: t.tasksOpen, icon: TYPE_ICON.TASK },
    { label: "Leads touched", value: t.leadsTouched },
    { label: "Contacts touched", value: t.contactsTouched },
    { label: "Deals advanced", value: t.dealsAdvanced },
  ];
  const dispo = Object.entries(data.callsByDisposition).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold">{data.user.name}</h2>
        <Badge variant="outline">{data.user.role.replace(/_/g, " ").toLowerCase()}</Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{c.icon}{c.label}</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">{c.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
        <div className="space-y-4">
          {dispo.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="mb-2 text-sm font-semibold">Calls by outcome</h3>
                <ul className="space-y-1 text-sm">
                  {dispo.map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span className="text-muted-foreground">{k.replace(/_/g, " ").toLowerCase()}</span>
                      <span className="tabular-nums">{v}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          <AssignTask userId={data.user.id} userName={data.user.name} qc={qc} />
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="border-b px-4 py-2 text-sm font-semibold">Recent activity</div>
            {data.recent.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Nothing logged in this range.</p>}
            <ul className="divide-y">
              {data.recent.map((a) => {
                const linked = a.contact ?? a.lead ?? a.deal;
                const when = a.completedAt ?? a.createdAt;
                return (
                  <li key={a.id} className="flex items-start gap-2 px-4 py-2 text-sm">
                    <span className="mt-0.5">{TYPE_ICON[a.type] ?? <StickyNote className="h-4 w-4" />}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{a.subject}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(when).toLocaleString()}
                        {linked ? ` · ${linked.name}` : ""}
                        {a.disposition ? ` · ${a.disposition.replace(/_/g, " ").toLowerCase()}` : ""}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AssignTask({ userId, userName, qc }: { userId: string; userName: string; qc: ReturnType<typeof useQueryClient> }) {
  const [subject, setSubject] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [done, setDone] = useState(false);

  const mut = useMutation({
    mutationFn: async () =>
      api.post("/tasks", { subject, ownerId: userId, dueDate: dueDate || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/reports/rep-activity"] });
      setSubject(""); setDueDate(""); setDone(true);
      setTimeout(() => setDone(false), 2500);
    },
  });

  return (
    <Card>
      <CardContent className="p-4 space-y-2">
        <h3 className="text-sm font-semibold">Assign a task to {userName.split(" ")[0]}</h3>
        <Input placeholder="Task subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9" />
        <div className="flex items-center gap-2">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-9" />
          <Button size="sm" disabled={!subject || mut.isPending} onClick={() => mut.mutate()}>
            <Plus className="mr-1 h-4 w-4" />{mut.isPending ? "Assigning…" : "Assign"}
          </Button>
        </div>
        {done && <p className="text-xs text-green-600">Task assigned.</p>}
        {mut.isError && <p className="text-xs text-destructive">Could not assign task.</p>}
      </CardContent>
    </Card>
  );
}
